// Optional Rollbar import - only load if package is available and enabled
let Rollbar: any = null;
let rollbar: any = null;

// Check if Rollbar is enabled first
const isRollbarEnabled = process.env.ENABLE_ROLLBAR !== 'false';

if (isRollbarEnabled) {
  try {
    // Use dynamic require to avoid bundling issues
    Rollbar = require('rollbar');
    
    // Initialize Rollbar only if package is available
    rollbar = new Rollbar({
      accessToken: process.env.ROLLBAR_SERVER_TOKEN,
      environment: process.env.NODE_ENV || 'development',
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        client: {
          javascript: {
            source_map_enabled: true,
            code_version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
          },
        },
      },
    });
  } catch (e) {
    // Rollbar package not available or failed to initialize - that's okay
    console.log('Rollbar not available, error reporting will be disabled:', e);
    Rollbar = null;
    rollbar = null;
  }
}

// Messages that originate from Next.js's App Router prefetch fallback, not real bugs.
// Next.js logs these when an RSC payload fetch fails (deploy in flight, flaky mobile
// network, ad blocker) and silently falls back to a full browser navigation — the user
// still reaches the page. Surfacing them in Rollbar drowns out real errors.
const IGNORED_CLIENT_MESSAGES = [
  'Failed to fetch RSC payload',
];

// Defensive: Rollbar 2.x passes a non-array `args` in some internal flows.
// `.map` on a non-array throws, which Rollbar reports via console.error — and
// our patched console.error in globalErrorHandler re-reports it, recursing
// until the stack blows. Never throw from this predicate.
export function shouldIgnoreClientMessage(args: unknown): boolean {
  try {
    const arr = Array.isArray(args) ? args : [args];
    const message = arr
      .map((a) => (typeof a === 'string' ? a : (a as any)?.message ?? ''))
      .join(' ');
    return IGNORED_CLIENT_MESSAGES.some((needle) => message.includes(needle));
  } catch {
    return false;
  }
}

// Memoised client-side Rollbar instance. Constructing a new Rollbar registers
// global window error listeners (captureUncaught / captureUnhandledRejections),
// so creating one per error report duplicates listeners and reports.
let cachedClientRollbar: any = null;

// Client-side Rollbar initialization
export const initClientRollbar = () => {
  if (cachedClientRollbar) {
    return cachedClientRollbar;
  }
  if (typeof window !== 'undefined' && isRollbarEnabled && Rollbar) {
    try {
      const clientRollbar = new Rollbar({
        accessToken: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
        environment: process.env.NODE_ENV || 'development',
        captureUncaught: true,
        captureUnhandledRejections: true,
        checkIgnore: (_isUncaught: boolean, args: unknown, _payload: unknown) =>
          shouldIgnoreClientMessage(args),
        payload: {
          client: {
            javascript: {
              source_map_enabled: true,
              code_version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
            },
          },
        },
      });
      cachedClientRollbar = clientRollbar;
      return clientRollbar;
    } catch (e) {
      console.log('Failed to initialize client Rollbar:', e);
      return null;
    }
  }
  return null;
};

// Server-side error reporting
export const reportError = (
  error: Error | string,
  context?: {
    operation?: string;
    userId?: string;
    additionalData?: any;
    req?: any;
  }
) => {
  if (!isRollbarEnabled) {
    console.log('Rollbar disabled - error logged to console:', {
      error: typeof error === 'string' ? error : error.message,
      context: context?.operation,
    });
    return;
  }

  try {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stackTrace = error instanceof Error ? error.stack : undefined;

    let contextString = context?.operation || 'Unknown operation';
    if (context?.req?.url) {
      contextString += ` | API Route: ${context.req.url}`;
    }

    rollbar?.error(errorMessage, {
      error: error instanceof Error ? error : new Error(error),
      context: contextString,
      user: context?.userId ? { id: context.userId } : undefined,
      custom: context?.additionalData || {},
    });

    console.error('Error reported to Rollbar:', {
      message: errorMessage,
      stack: stackTrace,
      context: contextString,
      user: context?.userId,
    });
  } catch (reportError) {
    console.error('Failed to report error to Rollbar:', reportError);
  }
};

// Client-side error reporting
export const reportClientError = (
  error: Error | string,
  context?: {
    component?: string;
    operation?: string;
    additionalData?: any;
  }
) => {
  if (!isRollbarEnabled) {
    console.log('Rollbar disabled - client error logged to console:', {
      error: typeof error === 'string' ? error : error.message,
      context: `${context?.component || 'Unknown Component'} - ${context?.operation || 'Unknown Operation'}`,
    });
    return;
  }

  try {
    const clientRollbar = initClientRollbar();
    if (!clientRollbar) {
      console.error('Rollbar not available on client side');
      return;
    }

    const errorMessage = typeof error === 'string' ? error : error.message;
    const stackTrace = error instanceof Error ? error.stack : undefined;

    const contextString = `Client: ${context?.component || 'Unknown Component'} - ${context?.operation || 'Unknown Operation'}`;

    clientRollbar.error(errorMessage, {
      error: error instanceof Error ? error : new Error(error),
      context: contextString,
      custom: context?.additionalData || {},
    });

    console.error('Client error reported to Rollbar:', {
      message: errorMessage,
      stack: stackTrace,
      context: contextString,
    });
  } catch (reportError) {
    console.error('Failed to report client error to Rollbar:', reportError);
  }
};

export default rollbar; 